<template>
  <button
    class="ui-btn"
    :type="buttonType"
    :class="[cssClass, getBrowserInfo, getPlatformInfo, iconName ? 'use-icon' : 'no-icon']"
    :disabled="disabled"
    @click="ohClick"
    @mouseover="onMouseOver"
    @mouseleave="onMouseLeave">
    <i v-if="iconName" class="icon" :class="iconName"/>
    <span v-if="!hideLabel" class="text"><slot/></span>
  </button>
</template>

<script>
import { browserInfo } from '@/libs/utils'

export default {
  name: 'UiButton',
  props: {
    buttonType: {
      type: String,
      default: 'button'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    primary: {
      type: Boolean,
      default: false
    },
    primary2: {
      type: Boolean,
      default: false
    },
    primary3: {
      type: Boolean,
      default: false
    },
    primary4: {
      type: Boolean,
      default: false
    },
    noBorder: {
      type: Boolean,
      default: false
    },
    noBackground: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'normal'
    },
    iconName: {
      type: String,
      required: false
    },
    hoverIconName: {
      type: String,
      required: false
    },
    hideLabel: {
      type: Boolean,
      default: false
    },
    circle: {
      type: Boolean,
      default: false
    },
    to: {
      type: Object,
      required: false
    },
    replace: {
      type: Boolean,
      default: false
    },
    go: {
      type: Number,
      required: false
    }
  },
  data () {
    return {
      iconSize: null,
      icon: this.iconName
    }
  },
  computed: {
    cssClass () {
      const value = {
        disabled: !!this.disabled,
        primary: !!this.primary,
        primary2: !!this.primary2,
        primary3: !!this.primary3,
        primary4: !!this.primary4,
        'no-border': !!this.noBorder,
        'no-background': !!this.noBackground,
        circle: !!this.circle
      }
      value[`size--${this.size}`] = true
      return value
    },
    getBrowserInfo () {
      return 'browser-' + browserInfo.name
    },
    getPlatformInfo () {
      return 'platform-' + browserInfo.platform
    }
  },
  mounted () {
    const iconEl = this.$el.querySelector('icon')

    if (iconEl) {
      this.iconSize = {
        width: iconEl.offsetWidth,
        height: iconEl.offsetHeight
      }
    }
  },
  methods: {
    ohClick (event) {
      if (this.disabled) {
        return
      }

      if (this.go) {
        this.$router.go(this.go)
      } else if (this.to) {
        if (this.replace) {
          this.$router.replace(this.to)
        } else {
          this.$router.push(this.to)
        }
      }
      this.$emit('click', event)
    },
    onMouseOver () {
      if (this.hoverIconName) {
        this.icon = this.hoverIconName
      }
    },
    onMouseLeave () {
      this.icon = this.iconName
    }
  }
}
</script>

<style lang="scss" scoped>
.ui-btn {
  position: relative;
  display: inline-block;
  background-color: #fff;
  border: 1px solid #bbb;
  box-sizing: border-box;
  white-space: nowrap;
  text-align: center;
  overflow: hidden;
  color: #444;

  &:hover {
    & > .text {
      z-index: 1;
    }
    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.06);
    }
  }

  &:active {
    & > .text {
      z-index: 1;
    }
    &:after {
      background-color: rgba(0, 0, 0, 0.16);
    }
  }

  &:not(.primary) {
    &.disabled {
      opacity: 0.2;
    }
  }

  &.disabled {
    pointer-events: none;
  }

  &.primary {
    border: 0;
    background-color: $brandColor;

    &:hover {
      &:after {
        background-color: rgba(0, 0, 0, 0.16);
      }
    }

    &:active {
      &:after {
        background-color: rgba(0, 0, 0, 0.32);
      }
    }

    &.disabled {
      background-color: rgba(0, 0, 0, 0.16) !important;

      & > .text,
      & > .icon {
        opacity: 0.85;
      }
    }

    & > .text {
      color: #fff;
    }
  }

  &.primary2 {
    border-color: $brandColor;
    background-color: #fff;

    &:hover {
      background-color: $brandColor;

      & > .text {
        color: #fff;
      }
    }

    &:active {
      background-color: $brandColor;
      &:after {
        background-color: rgba(0, 0, 0, 0.16);
      }
      & > .text {
        color: #fff;
      }
    }

    & > .text {
      color: $brandColor;
    }
  }

  &.primary3 {
    border-color: #bbb;
    background-color: #fff;

    &:hover {
      &:after {
        background-color: rgba(0, 0, 0, 0.06);
      }
    }

    &:active {
      &:after {
        background-color: rgba(0, 0, 0, 0.16);
      }
    }

    .text {
      color: #444;
    }
  }

  &.primary4 {
    border-color: #1199af;
    background-color: #1199af;

    &:hover {
      &:after {
        background-color: rgba(0, 0, 0, 0.06);
      }
    }

    &:active {
      &:after {
        background-color: rgba(0, 0, 0, 0.16);
      }
    }

    .text {
      color: #fff;
    }
  }

  &.no-border {
    border: none;
  }

  &.no-background {
    background: transparent;
  }

  &.size--large {
    min-width: 100px;
    height: 38px;
    border-radius: 20px;
    padding: 0 15px;
    box-sizing: border-box;
    font-size: 14px;
    font-weight: bold;
  }

  &.size--normal {
    border-radius: 20px;
    height: 38px;
    padding: 0 15px;
    font-size: 14px;
    font-weight: bold;
    min-width: 70px;

    &.circle {
      width: 38px;
      min-width: auto;
      padding: 0;
    }
  }

  &.size--small {
    border-radius: 20px;
    height: 34px;
    padding: 0 13px;
    min-width: 60px;

    &.use-icon:not(.circle) {
      padding-left: calc(11px + 16px + 5px);
      & > .text {
        font-size: 13px;
      }
    }

    &.circle {
      width: 34px;
      min-width: auto;
      padding: 0;
    }
  }

  & > .text {
    position: relative;
    top: -1px;
  }

  & > .icon {
    background-color: transparent;
    position: absolute;
    top: 50%;
    left: 11px;
    transform: translateY(-50%);
  }

  &.use-icon:not(.circle) {
    padding: 0;
    padding-left: calc(11px + 16px + 8px);
    padding-right: 13px;
    text-align: right;

    & > .text {
      margin-left: 0;
    }
  }

  &.use-icon.circle {
    & > .icon {
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
}

html[lang='en'] {
  .ui-btn > .text {
    top: 0;
  }
}
</style>
